import { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useFinance } from '@/hooks/useFinance';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import {
  INVOICE_STATUSES,
  INVOICE_STATUS_META,
  formatDate,
  formatMoney,
  type FinanceInvoice,
  type FinanceInvoiceItem,
  type InvoiceStatus,
} from '@/lib/finance';
import { DOC_META, QUOTE_STATUS_META, type DocType } from '@/lib/invoiceDoc';
import { downloadInvoicePdf } from '@/lib/invoicePdf';
import { reserveDocNumber } from '@/lib/invoiceNumbering';
import InvoiceShareMenu from '@/pages/dashboard/finance/components/InvoiceShareMenu';
import InvoicePaymentModal from '@/pages/dashboard/finance/components/InvoicePaymentModal';
import InvoiceReminderModal from '@/pages/dashboard/finance/components/InvoiceReminderModal';
import InvoiceImportModal from '@/pages/dashboard/finance/components/InvoiceImportModal';
import { resolveAiAccess, type AiAccess } from '@/lib/aiAccess';
import { daysLate, isOverdue, needsReminder, logReminder, reminderMailto, buildReminderMessage } from '@/lib/invoicePayments';
import { buildExcelDocument } from '@/lib/excelExport';
import { FinanceSection, FinanceEmpty, FinanceStatCard } from '@/pages/dashboard/finance/components/FinanceUI';

export default function InvoiceListPanel({ docType }: { docType: DocType }) {
  const meta = DOC_META[docType];
  const isQuote = docType === 'quote';
  const { user } = useAuth();
  const finance = useFinance();
  const company = useCompanyProfile();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const createdId = params.get('created');

  const { currency, invoices, loading, settings } = finance;
  const [statusFilter, setStatusFilter] = useState<'all' | InvoiceStatus>('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [paymentInvoice, setPaymentInvoice] = useState<FinanceInvoice | null>(null);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [convertingId, setConvertingId] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [creatingExcel, setCreatingExcel] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [aiAccess, setAiAccess] = useState<AiAccess | null>(null);

  // L'import de facture par OCR n'est proposé qu'aux forfaits compatibles IA.
  useEffect(() => {
    let cancelled = false;
    resolveAiAccess(user ? { id: user.id, typecompte: user.typecompte } : null).then((access) => {
      if (!cancelled) setAiAccess(access);
    });
    return () => { cancelled = true; };
  }, [user]);

  const statusMeta = isQuote ? QUOTE_STATUS_META : INVOICE_STATUS_META;

  const docs = useMemo(
    () => invoices.filter((i) => (i.doc_type || 'invoice') === docType),
    [invoices, docType],
  );
  const filtered = useMemo(() => {
    const byStatus = statusFilter === 'all' ? docs : docs.filter((i) => i.status === statusFilter);
    if (clientFilter === 'all') return byStatus;
    return byStatus.filter((i) => (i.customer_name || '') === clientFilter);
  }, [docs, statusFilter, clientFilter]);

  // Liste des clients présents dans les documents (filtre).
  const clientOptions = useMemo(() => {
    const names = new Set<string>();
    docs.forEach((i) => {
      const name = (i.customer_name || '').trim();
      if (name) names.add(name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [docs]);

  /** Factures en retard, prêtes à relancer. */
  const overdueInvoices = useMemo(
    () => docs.filter((i) => isOverdue(i)).sort((a, b) => daysLate(b) - daysLate(a)),
    [docs],
  );

  // Devis déjà transformés en facture (jamais deux fois le même).
  const invoiceByQuote = useMemo(() => {
    const map = new Map<number, FinanceInvoice>();
    invoices.forEach((i) => {
      if ((i.doc_type || 'invoice') === 'invoice' && i.source_quote_id) {
        map.set(i.source_quote_id, i);
      }
    });
    return map;
  }, [invoices]);

  const counts = useMemo(() => {
    const settled = docs
      .filter((i) => i.status === 'paid')
      .reduce((s, i) => s + Number(i.total), 0);
    const pending = docs
      .filter((i) => ['sent', 'partially_paid', 'overdue'].includes(i.status))
      .reduce((s, i) => s + Math.max(0, Number(i.total) - Number(i.amount_paid)), 0);
    return { settled, pending };
  }, [docs]);

  const exportPdf = async (inv: FinanceInvoice) => {
    setBusyId(inv.id);
    setMsg(null);
    try {
      const { data } = await supabase.from('finance_invoice_items').select('*').eq('invoice_id', inv.id);
      downloadInvoicePdf(inv, (data || []) as FinanceInvoiceItem[], company.profile);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Erreur export PDF');
    } finally {
      setBusyId(null);
    }
  };

  const handleOpenInExcel = async () => {
    if (!user || filtered.length === 0) return;
    setCreatingExcel(true);
    try {
      const headers = ['N°', 'Client', 'Téléphone', 'Email', 'Émission', 'Échéance', 'HT', 'Remise', 'TVA %', 'TTC', 'Payé', 'Statut'];
      const rows = filtered.map((inv) => [
        inv.number || '',
        inv.customer_name || '',
        inv.customer_phone || '',
        inv.customer_email || '',
        formatDate(inv.issue_date),
        inv.due_date ? formatDate(inv.due_date) : '',
        String(Number(inv.subtotal)),
        String(Number(inv.discount)),
        String(Number(inv.tax_rate)),
        String(Number(inv.total)),
        String(Number(inv.amount_paid)),
        statusMeta[inv.status]?.label || inv.status,
      ]);
      await buildExcelDocument(
        user.id,
        `${meta.plural} ${new Date().toISOString().slice(0, 10)}`,
        headers,
        rows,
        { sheetName: meta.plural },
      );
      navigate('/dashboard/file-manager');
    } catch {
      /* erreur gérée côté documents */
    } finally {
      setCreatingExcel(false);
    }
  };

  /** Transforme un devis accepté en facture, en reprenant client et lignes. */
  const convertQuote = async (quote: FinanceInvoice) => {
    if (invoiceByQuote.has(quote.id)) return;
    setConvertingId(quote.id);
    setMsg(null);
    try {
      const { data } = await supabase
        .from('finance_invoice_items')
        .select('*')
        .eq('invoice_id', quote.id);
      const items = (data || []) as FinanceInvoiceItem[];
      if (items.length === 0) {
        setMsg('Ce devis ne contient aucune ligne : ajoutez des produits ou services avant de le facturer.');
        return;
      }
      const number = await reserveDocNumber({
        docType: 'invoice',
        merchantId: finance.merchantId,
        profile: company.profile,
        invoices,
        invoicePrefix: settings?.invoice_prefix || company.profile.invoice_prefix,
        reserveInvoiceNumber: company.reserveInvoiceNumber,
      });
      const created = await finance.createInvoice({
        number,
        doc_type: 'invoice',
        customer_name: quote.customer_name || 'Client',
        customer_phone: quote.customer_phone,
        customer_email: quote.customer_email,
        customer_address: quote.customer_address ?? null,
        customer_ice: quote.customer_ice ?? null,
        issue_date: new Date().toISOString().slice(0, 10),
        sale_date: quote.sale_date ?? null,
        due_date: quote.due_date ?? null,
        reference: quote.number,
        source_quote_id: quote.id,
        payment_terms: quote.payment_terms ?? null,
        payment_method: quote.payment_method ?? null,
        legal_mentions: quote.legal_mentions ?? null,
        status: 'draft',
        discount: Number(quote.discount) || 0,
        tax_rate: Number(quote.tax_rate) || 0,
        notes: quote.notes ?? null,
        items: items.map((it) => ({
          description: it.description,
          quantity: Number(it.quantity) || 1,
          unit_price: Number(it.unit_price) || 0,
          discount: Number(it.discount) || 0,
          item_type: it.item_type ?? 'custom',
          product_id: it.product_id ?? null,
          subtotal: Math.max(
            0,
            (Number(it.quantity) || 1) * (Number(it.unit_price) || 0) - (Number(it.discount) || 0),
          ),
        })),
      });
      setMsg(`Devis ${quote.number} transformé en facture ${created.number}.`);
      navigate(`/dashboard/finance/factures?created=${created.id}`);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Impossible de transformer ce devis en facture.');
    } finally {
      setConvertingId(null);
    }
  };

  /** Relance e-mail en un clic : message pré-rempli + historisation. */
  const remindByEmail = async (inv: FinanceInvoice) => {
    if (!inv.customer_email) {
      setMsg(`Aucune adresse e-mail pour ${inv.customer_name || 'ce client'} : ajoutez-la sur la facture pour relancer.`);
      return;
    }
    window.location.href = reminderMailto(inv, company.profile);
    await logReminder({
      invoiceId: inv.id,
      merchantId: finance.merchantId,
      channel: 'email',
      recipient: inv.customer_email,
      message: buildReminderMessage(inv, company.profile),
    });
    setMsg(`Relance e-mail préparée pour ${inv.number} (${inv.customer_email}).`);
  };

  const changeStatus = async (inv: FinanceInvoice, status: InvoiceStatus) => {
    if (status === inv.status) return;
    try {
      await finance.updateInvoiceStatus(inv.id, status);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Erreur');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <FinanceStatCard label={meta.plural} value={String(docs.length)} icon={meta.icon} tone="secondary" />
        <FinanceStatCard label={isQuote ? 'Acceptés' : 'Encaissé'} value={formatMoney(counts.settled, currency)} icon="ri-check-double-line" tone="primary" />
        <FinanceStatCard label={isQuote ? 'En attente' : 'À encaisser'} value={formatMoney(counts.pending, currency)} icon="ri-time-line" tone="accent" />
        <FinanceStatCard
          label="TVA appliquée"
          value={company.profile.vat_subject ? `${company.profile.default_vat_rate ?? settings?.default_tax_rate ?? 0} %` : 'Non assujetti'}
          icon="ri-percent-line"
          tone="secondary"
          hint={company.profile.vat_subject ? 'Assujetti à la TVA' : 'TVA 0 %'}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Link
          to={`${meta.basePath}/nouvelle`}
          className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer no-underline"
        >
          <i className="ri-add-line"></i>{meta.newLabel}
        </Link>
        {!isQuote && aiAccess?.allowed && (
          <button
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-secondary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer"
            title="Importer une facture (photo ou PDF) — lecture automatique par IA"
          >
            <i className="ri-scan-2-line"></i>Importer (IA)
          </button>
        )}
        <button
          onClick={handleOpenInExcel}
          disabled={creatingExcel || filtered.length === 0}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer"
        >
          {creatingExcel ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-file-excel-2-line"></i>}Ouvrir dans Excel
        </button>
        <Link
          to="/dashboard/finance/parametres"
          className="flex items-center gap-1.5 px-3.5 py-2 bg-background-50 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer no-underline"
        >
          <i className="ri-settings-3-line"></i>Paramètres facturation
        </Link>
        {!isQuote && (
          <button
            onClick={() => setReminderOpen(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-background-50 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
            title="Relancer les factures impayées (e-mail + WhatsApp)"
          >
            <i className="ri-mail-send-line"></i>Relances
            {overdueInvoices.length > 0 && (
              <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
                {overdueInvoices.length}
              </span>
            )}
          </button>
        )}
      </div>

      {createdId && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-50 text-primary-800 text-sm">
          <i className="ri-checkbox-circle-line"></i>
          {meta.label} créée avec succès.
        </div>
      )}
      {msg && <div className="px-4 py-2.5 rounded-lg bg-secondary-50 text-secondary-800 text-sm">{msg}</div>}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 overflow-x-auto max-w-full">
          {(['all', ...INVOICE_STATUSES] as ('all' | InvoiceStatus)[]).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${statusFilter === s ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
            >
              {s === 'all' ? 'Toutes' : statusMeta[s].label}
            </button>
          ))}
        </div>
        <div className="relative">
          <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="pl-8 pr-8 py-2 rounded-full text-xs font-medium bg-background-50 border border-background-200/70 text-foreground-700 cursor-pointer focus:outline-none max-w-[220px]"
            title="Filtrer par client"
          >
            <option value="all">Tous les clients</option>
            {clientOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
        {clientFilter !== 'all' && (
          <button
            onClick={() => setClientFilter('all')}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-line"></i>Effacer
          </button>
        )}
      </div>

      <FinanceSection title={`${meta.plural} (${filtered.length})`} icon={meta.icon}>
        {filtered.length === 0 ? (
          <FinanceEmpty
            icon={meta.icon}
            title={`Aucune ${meta.label.toLowerCase()}`}
            hint="Renseignez un client puis ajoutez vos produits ou services."
            action={
              <Link to={`${meta.basePath}/nouvelle`} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors no-underline">
                {meta.newLabel}
              </Link>
            }
          />
        ) : (
          <div className="divide-y divide-background-200/70">
            {filtered.map((inv) => {
              const sm = statusMeta[inv.status];
              const invoicedQuote = invoiceByQuote.get(inv.id);
              return (
                <div key={inv.id} className="flex items-center gap-3 py-3 flex-wrap sm:flex-nowrap">
                  <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                    <i className={`${meta.icon} text-sm`}></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground-900 truncate">{inv.number} · {inv.customer_name || 'Client'}</div>
                    <div className="text-xs text-foreground-400 flex items-center gap-2 flex-wrap">
                      <span>{formatDate(inv.issue_date)}</span>
                      {inv.due_date && <span>{isQuote ? 'Validité' : 'Échéance'} {formatDate(inv.due_date)}</span>}
                      <span className={`px-1.5 py-0.5 rounded-full ${sm.className}`}>{sm.label}</span>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-foreground-900 whitespace-nowrap">{formatMoney(Number(inv.total), inv.currency || currency)}</span>
                  {!isQuote && Number(inv.amount_paid) > 0 && needsReminder(inv) && (
                    <span className="text-[11px] text-accent-700 bg-accent-50 px-2 py-0.5 rounded-full whitespace-nowrap" title="Reste à encaisser">
                      reste {formatMoney(Math.max(0, Number(inv.total) - Number(inv.amount_paid)), inv.currency || currency)}
                    </span>
                  )}
                  {isQuote && invoicedQuote && (
                    <span
                      className="flex items-center gap-1 px-2 h-7 rounded-md bg-primary-100 text-primary-700 text-[11px] font-medium whitespace-nowrap"
                      title={`Déjà facturé : ${invoicedQuote.number}`}
                    >
                      <i className="ri-file-text-line"></i>Facturé
                    </span>
                  )}
                  {isQuote && !invoicedQuote && inv.status === 'paid' && (
                    <button
                      onClick={() => convertQuote(inv)}
                      disabled={convertingId === inv.id}
                      className="flex items-center gap-1 px-2.5 h-7 rounded-md bg-primary-500 text-background-50 text-[11px] font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
                      title="Transformer ce devis accepté en facture"
                    >
                      <i className={convertingId === inv.id ? 'ri-loader-4-line animate-spin' : 'ri-file-transfer-line'}></i>
                      Facturer
                    </button>
                  )}
                  <InvoiceShareMenu invoice={inv} profile={company.profile} onNote={setMsg} />
                  {!isQuote && needsReminder(inv) && (
                    <button
                      onClick={() => setPaymentInvoice(inv)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                      title="Enregistrer un paiement (total ou partiel)"
                    >
                      <i className="ri-hand-coin-line text-sm"></i>
                    </button>
                  )}
                  {!isQuote && isOverdue(inv) && (
                    <button
                      onClick={() => remindByEmail(inv)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer"
                      title={`Relancer par e-mail (retard de ${daysLate(inv)} jour(s))`}
                    >
                      <i className="ri-mail-send-line text-sm"></i>
                    </button>
                  )}
                  <button
                    onClick={() => exportPdf(inv)}
                    disabled={busyId === inv.id}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer disabled:opacity-50"
                    title="Exporter en PDF"
                  >
                    <i className={busyId === inv.id ? 'ri-loader-4-line animate-spin text-sm' : 'ri-file-pdf-2-line text-sm'}></i>
                  </button>
                  <select
                    value={inv.status}
                    onChange={(e) => changeStatus(inv, e.target.value as InvoiceStatus)}
                    className="px-2 py-1 rounded-lg text-xs bg-background-50 border border-background-200/70 text-foreground-700 cursor-pointer focus:outline-none"
                  >
                    {INVOICE_STATUSES.map((s) => <option key={s} value={s}>{statusMeta[s].label}</option>)}
                  </select>
                  <button
                    onClick={() => finance.deleteInvoice(inv.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                    title="Supprimer"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </FinanceSection>

      {paymentInvoice && (
        <InvoicePaymentModal
          invoice={paymentInvoice}
          currency={currency}
          onClose={() => setPaymentInvoice(null)}
          onDone={(message) => {
            setMsg(message);
            finance.reload();
          }}
        />
      )}

      {reminderOpen && (
        <InvoiceReminderModal
          invoices={docs}
          profile={company.profile}
          merchantId={finance.merchantId}
          onClose={() => setReminderOpen(false)}
          onDone={setMsg}
        />
      )}

      {importOpen && (
        <InvoiceImportModal
          onClose={() => setImportOpen(false)}
          onDone={(message) => { setMsg(message); finance.reload(); }}
        />
      )}

      <p className="text-[11px] text-foreground-400 flex items-start gap-1.5">
        <i className="ri-information-line mt-0.5"></i>
        Zifek Finance n&apos;est pas un système de comptabilité ou de conformité fiscale certifié. Les champs de TVA et de taxes sont configurables et à adapter selon la
        réglementation marocaine applicable.
      </p>
    </div>
  );
}